<template>
    <div class="container tercero-page">
        <div class="param-title-wrap">
            <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" />
            </b-button>
            <h2 class="param-title">{{ labels.title }}</h2>
        </div>
        
        <!-- Botones de alternancia -->
        <div class="toggle-buttons-container mt-3">
            <div class="toggle-buttons">
                <b-button
                    class="toggle-btn"
                    :class="{ 'active': vistaActiva === 'activo' }"
                    @click="cuotasActivos"
                >
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="mr-1" />
                    {{ labels.activoPorDerechoUso }}
                </b-button>
                <b-button
                    class="toggle-btn"
                    :class="{ 'active': vistaActiva === 'pasivo' }"
                    @click="cuotasPasivos"
                >
                    <font-awesome-icon :icon="['fas', 'chart-pie']" class="mr-1" />
                    {{ labels.pasivoPorArrendamiento }}
                </b-button>
            </div>
        </div>

        <!-- Vista Activo por derecho de uso -->
        <div v-if="vistaActiva === 'activo'" class="mt-4">
            <div v-if="!isLoading">
                <CorporateDataTable
                    :key="tableKeyActivo"
                    :items="listCuotas"
                    :fields="allFieldsActivos"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefsActivos"
                    :toolbar-disabled="!showToolbarExtras"
                    :enable-row-click="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-excel-btn"
                                @click="exportarExcelActivo()"
                                :disabled="!showToolbarExtras || listCuotas.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                            </b-button>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
            
            <!-- Sin datos -->
            <div v-if="!isLoading && listCuotas.length === 0" class="text-center py-5">
                <p class="mt-2">{{ labels.noData }}</p>
            </div>
        </div>

        <!-- Vista Pasivo por arrendamiento -->
        <div v-if="vistaActiva === 'pasivo'" class="mt-4">
            <div v-if="!isLoading">
                <CorporateDataTable
                    :key="tableKeyPasivo"
                    :items="listCuotas"
                    :fields="allFieldsPasivos"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefsPasivos"
                    :toolbar-disabled="!showToolbarExtras"
                    :enable-row-click="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-excel-btn"
                                @click="exportarExcelPasivo()"
                                :disabled="!showToolbarExtras || listCuotas.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                            </b-button>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
            
            <!-- Sin datos -->
            <div v-if="!isLoading && listCuotas.length === 0" class="text-center py-5">
                <p class="mt-2">{{ labels.noData }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import CorporateDataTable from '../utils/CorporateDataTable.vue';
import Contrato from '../../services/Contrato'; 
import ContratoCuota from '../../services/ContratoCuota'; 
import TipoContrato from '../../services/TipoContrato'; 
import TipoActivo from '../../services/TipoActivo';
import * as XLSX from "exceljs";

export default {
    name: 'ConsultarCuotas',
    components: { CorporateDataTable },
    data() {
        return {
            vistaActiva: 'activo', // 'activo' o 'pasivo'
            isLoading: false,
            showToolbarExtras: false,
            tableKeyActivo: 0,
            tableKeyPasivo: 0,
            listCuotas: [],
            listaContratos: [],
            valorCanon: '',
            canonArrendamiento: null,
            infoContrato: [],
            lstTipoContratos: [],
            lstTipoActivo: [],
            numContrato: null
        }
    },
    computed: {
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: 'Cuotas',
                    activoPorDerechoUso: 'Activo por derecho de uso',
                    pasivoPorArrendamiento: 'Pasivo por arrendamiento',
                    loading: 'Cargando...',
                    noData: 'No hay datos disponibles',
                    noMatches: 'No se encontraron resultados',
                    searchPlaceholder: 'Buscar en la tabla...',
                    rowsPerPage: 'Filas por página:',
                    noSort: 'Sin orden',
                    sortAsc: 'A-Z / Menor a mayor',
                    sortDesc: 'Z-A / Mayor a menor',
                    columns: 'Columnas',
                    showAllColumns: 'Mostrar todas',
                    filters: 'Filtros',
                    clearFilters: 'Limpiar filtros',
                    cuota: 'Nro.',
                    fechaPago: 'Fecha cuota',
                    tasa: 'Tasa',
                    valorCanon: 'Valor canon',
                    activoDerUsoBrt: 'Derecho uso Bruto',
                    gastoAmortizacion: 'Gasto amortización',
                    amortizacionAcumulad: 'Amortización acumulada',
                    cambioPasivo: 'Cambio pasivo',
                    activoDerecUsoNeto: 'Derecho uso Neto',
                    basePasivArrendamiento: 'Pasivo inicial',
                    pagoCapital: 'Pago capital',
                    intereses: 'Intereses',
                    pasivoArrendamiento: 'Pasivo final',
                    filtrarNro: 'Filtrar Nro...',
                    filtrarFecha: 'Filtrar fecha...',
                    filtrarTasa: 'Filtrar tasa...',
                    filtrarValorCanon: 'Filtrar valor canon...',
                    filtrarActivoBruto: 'Filtrar activo bruto...',
                    filtrarGastoAmort: 'Filtrar gasto amortización...',
                    filtrarAmortAcum: 'Filtrar amortización acumulada...',
                    filtrarCambioPasivo: 'Filtrar cambio pasivo...',
                    filtrarActivoNeto: 'Filtrar activo neto...',
                    filtrarPasivoInicial: 'Filtrar pasivo inicial...',
                    filtrarPagoCapital: 'Filtrar pago capital...',
                    filtrarIntereses: 'Filtrar intereses...',
                    filtrarPasivoFinal: 'Filtrar pasivo final...'
                }
            } else {
                return {
                    title: 'Installments',
                    activoPorDerechoUso: 'Right-of-use asset',
                    pasivoPorArrendamiento: 'Lease liability',
                    loading: 'Loading...',
                    noData: 'No data available',
                    noMatches: 'No results found',
                    searchPlaceholder: 'Search in table...',
                    rowsPerPage: 'Rows per page:',
                    noSort: 'No sorting',
                    sortAsc: 'A-Z / Low to high',
                    sortDesc: 'Z-A / High to low',
                    columns: 'Columns',
                    showAllColumns: 'Show all',
                    filters: 'Filters',
                    clearFilters: 'Clear filters',
                    cuota: '#',
                    fechaPago: 'Date',
                    tasa: 'Rate',
                    valorCanon: 'Canon value',
                    activoDerUsoBrt: 'Gross right-of-use',
                    gastoAmortizacion: 'Depreciation expense',
                    amortizacionAcumulad: 'Accumulated amortization',
                    cambioPasivo: 'Passive change',
                    activoDerecUsoNeto: 'Net right-of-use',
                    basePasivArrendamiento: 'Initial liability',
                    pagoCapital: 'Capital payment',
                    intereses: 'Interest',
                    pasivoArrendamiento: 'Final liability',
                    filtrarNro: 'Filter #...',
                    filtrarFecha: 'Filter date...',
                    filtrarTasa: 'Filter rate...',
                    filtrarValorCanon: 'Filter canon value...',
                    filtrarActivoBruto: 'Filter gross asset...',
                    filtrarGastoAmort: 'Filter depreciation...',
                    filtrarAmortAcum: 'Filter accumulated amortization...',
                    filtrarCambioPasivo: 'Filter passive change...',
                    filtrarActivoNeto: 'Filter net asset...',
                    filtrarPasivoInicial: 'Filter initial liability...',
                    filtrarPagoCapital: 'Filter capital payment...',
                    filtrarIntereses: 'Filter interest...',
                    filtrarPasivoFinal: 'Filter final liability...'
                }
            }
        },

        allFieldsActivos() {
            return [
                { 
                    key: 'numero_cuota', 
                    label: this.labels.cuota, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-center align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '70px' } 
                },
                { 
                    key: 'fecha_pago', 
                    label: this.labels.fechaPago, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-center align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '110px' },
                    formatter: (value) => this.formatFecha(value)
                },
                { 
                    key: 'tasa', 
                    label: this.labels.tasa, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '80px' },
                    formatter: (value) => this.formatDecimal(value)
                },
                { 
                    key: 'valor_canon', 
                    label: this.labels.valorCanon, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'activo_derecho_uso_bruto', 
                    label: this.labels.activoDerUsoBrt, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'gasto_amortizacion', 
                    label: this.labels.gastoAmortizacion, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '130px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'amortizacion_acumulada', 
                    label: this.labels.amortizacionAcumulad, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'cambio_pasivo', 
                    label: this.labels.cambioPasivo, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'activo_derecho_uso_neto', 
                    label: this.labels.activoDerecUsoNeto, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' },
                    formatter: (value) => this.formatNumber(value)
                },
            ];
        },

        allFieldsPasivos() {
            return [
                { 
                    key: 'numero_cuota', 
                    label: this.labels.cuota, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-center align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '70px' } 
                },
                { 
                    key: 'fecha_pago', 
                    label: this.labels.fechaPago, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-center align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '110px' },
                    formatter: (value) => this.formatFecha(value)
                },
                { 
                    key: 'tasa', 
                    label: this.labels.tasa, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '80px' },
                    formatter: (value) => this.formatDecimal(value)
                },
                { 
                    key: 'valor_canon', 
                    label: this.labels.valorCanon, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'base_pasivo_arrendamiento', 
                    label: this.labels.basePasivArrendamiento, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'cambio_pasivo', 
                    label: this.labels.cambioPasivo, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'pago_capital', 
                    label: this.labels.pagoCapital, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'intereses', 
                    label: this.labels.intereses, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '110px' },
                    formatter: (value) => this.formatNumber(value)
                },
                { 
                    key: 'pasivo_arrendamiento', 
                    label: this.labels.pasivoArrendamiento, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end align-middle', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '130px' },
                    formatter: (value) => this.formatNumber(value)
                },
            ];
        },

        tableFilterDefsActivos() {
            return [
                { key: 'numero_cuota', type: 'text', placeholder: this.labels.filtrarNro },
                { key: 'fecha_pago', type: 'text', placeholder: this.labels.filtrarFecha },
                { key: 'tasa', type: 'text', placeholder: this.labels.filtrarTasa },
                { key: 'valor_canon', type: 'text', placeholder: this.labels.filtrarValorCanon },
                { key: 'activo_derecho_uso_bruto', type: 'text', placeholder: this.labels.filtrarActivoBruto },
                { key: 'gasto_amortizacion', type: 'text', placeholder: this.labels.filtrarGastoAmort },
                { key: 'amortizacion_acumulada', type: 'text', placeholder: this.labels.filtrarAmortAcum },
                { key: 'cambio_pasivo', type: 'text', placeholder: this.labels.filtrarCambioPasivo },
                { key: 'activo_derecho_uso_neto', type: 'text', placeholder: this.labels.filtrarActivoNeto },
            ];
        },

        tableFilterDefsPasivos() {
            return [
                { key: 'numero_cuota', type: 'text', placeholder: this.labels.filtrarNro },
                { key: 'fecha_pago', type: 'text', placeholder: this.labels.filtrarFecha },
                { key: 'tasa', type: 'text', placeholder: this.labels.filtrarTasa },
                { key: 'valor_canon', type: 'text', placeholder: this.labels.filtrarValorCanon },
                { key: 'base_pasivo_arrendamiento', type: 'text', placeholder: this.labels.filtrarPasivoInicial },
                { key: 'cambio_pasivo', type: 'text', placeholder: this.labels.filtrarCambioPasivo },
                { key: 'pago_capital', type: 'text', placeholder: this.labels.filtrarPagoCapital },
                { key: 'intereses', type: 'text', placeholder: this.labels.filtrarIntereses },
                { key: 'pasivo_arrendamiento', type: 'text', placeholder: this.labels.filtrarPasivoFinal },
            ];
        },

        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            };
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        cuotasActivos() {
            this.vistaActiva = 'activo';
            this.tableKeyActivo += 1;
        },

        cuotasPasivos() {
            this.vistaActiva = 'pasivo';
            this.tableKeyPasivo += 1;
        },

        formatNumber(value) {
            if (value === undefined || value === null || value === '') return '';
            const num = parseFloat(value);
            if (isNaN(num)) return value;
            return new Intl.NumberFormat('es-CO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(num);
        },

        formatDecimal(value) {
            if (value === undefined || value === null || value === '') return '';
            const num = parseFloat(value);
            if (isNaN(num)) return value;
            return new Intl.NumberFormat('es-CO', {
                minimumFractionDigits: 6,
                maximumFractionDigits: 6
            }).format(num);
        },

        formatFecha(fecha) {
            if (!fecha) return '';
            try {
                // const date = new Date(fecha);
                // if (isNaN(date.getTime())) return fecha;
                // return new Intl.DateTimeFormat('es-CO', {
                //     year: 'numeric',
                //     month: '2-digit',
                //     day: '2-digit'
                // }).format(date);

                return fecha;
            } catch {
                return fecha;
            }
        },

        tipoContrato(numTipContrato) {
            const tipo = this.lstTipoContratos.find((tc) => tc.tipo_contrato == numTipContrato);
            return tipo?.descripcion || '';
        },

        tipoActivo() {
            const tipoAct = this.lstTipoActivo.find((ta) => ta.tipo_activo == this.infoContrato.tipo_activo);
            return tipoAct?.descripcion || '';
        },

        fechaActual() {
            const fecha = new Date();
            const año = fecha.getFullYear();
            const mes = String(fecha.getMonth() + 1).padStart(2, '0');
            const dia = String(fecha.getDate()).padStart(2, '0');
            return `${año}-${mes}-${dia}`;
        },

        async exportarExcelActivo() {
            try {
                this.isLoading = true;
                const { Workbook } = XLSX;
                const workbook = new Workbook();
                const worksheet = workbook.addWorksheet("Activo por derecho de uso");

                // Título
                worksheet.addRow(['Activo por derecho de uso']);
                worksheet.mergeCells('A1:I1');
                const titleCell = worksheet.getCell('A1');
                titleCell.font = { name: 'Arial', size: 12, bold: true };
                titleCell.alignment = { horizontal: 'center' };

                // Info general
                worksheet.addRow(['']);
                worksheet.mergeCells('A2:I2');
                const cell2 = worksheet.getCell('A2');
                cell2.value = {
                    richText: [
                        { text: 'Contrato: ', font: { bold: true } },
                        { text: `${this.numContrato} | ` },
                        { text: 'Tipo contrato: ', font: { bold: true } },
                        { text: `${this.tipoContrato(this.infoContrato.tipo_contrato)} | ` },
                        { text: 'Tipo activo: ', font: { bold: true } },
                        { text: `${this.tipoActivo()} | ` },
                        { text: 'Generado por: ', font: { bold: true } },
                        { text: `${this.$store.state.userName}` },
                    ]
                };
                cell2.font = { name: 'Arial', size: 12 };
                cell2.alignment = { horizontal: 'center', vertical: 'middle' };

                // Más info
                worksheet.addRow(['']);
                worksheet.mergeCells('A3:I3');
                const cell3 = worksheet.getCell('A3');
                cell3.value = {
                    richText: [
                        { text: 'Fecha inicial: ', font: { bold: true } },
                        { text: `${this.infoContrato.fecha_inicial || 'N/A'} | ` },
                        { text: 'Fecha final: ', font: { bold: true } },
                        { text: `${this.infoContrato.fecha_final || 'N/A'} | ` },
                        { text: 'Fecha generación: ', font: { bold: true } },
                        { text: `${this.fechaActual()}` },
                    ]
                };
                cell3.font = { name: 'Arial', size: 12 };
                cell3.alignment = { horizontal: 'center', vertical: 'middle' };

                worksheet.addRow(['']);
                worksheet.mergeCells('A4:I4');

                // Encabezados
                const headers = this.allFieldsActivos.map(f => f.label);
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { name: 'Arial', size: 11, bold: true };
                headerRow.alignment = { horizontal: 'center' };
                headerRow.eachCell(cell => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF0073B7' }
                    };
                    cell.font.color = { argb: 'FFFFFFFF' };
                });

                // Datos
                this.listCuotas.forEach(e => {
                    const fila = [];
                    this.allFieldsActivos.forEach(f => {
                        const valor = e[f.key];
                        if (f.key === 'fecha_pago') {
                            fila.push(valor || '');
                        } else if (f.key === 'tasa') {
                            fila.push(this.formatDecimal(valor));
                        } else {
                            fila.push(valor !== undefined && valor !== null ? valor : 0);
                        }
                    });
                    worksheet.addRow(fila);
                });

                const border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                worksheet.eachRow({ includeEmpty: false }, row => {
                    if ([1, 2, 3, 4].includes(row.number)) return;
                    row.eachCell(cell => {
                        cell.border = border;
                    });
                });

                worksheet.columns.forEach(column => {
                    column.width = 18;
                });

                const file = await workbook.xlsx.writeBuffer();
                const downloadLink = document.createElement('a');
                downloadLink.download = `Activo_Derecho_Uso_${this.numContrato}.xlsx`;
                downloadLink.href = window.URL.createObjectURL(
                    new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                );
                downloadLink.click();

                this.$toast.success('Excel generado exitosamente.');
            } catch (error) {
                console.error('Error exportando Excel:', error);
                this.$toast.error('Error al generar el Excel.');
            } finally {
                this.isLoading = false;
            }
        },

        async exportarExcelPasivo() {
            try {
                this.isLoading = true;
                const { Workbook } = XLSX;
                const workbook = new Workbook();
                const worksheet = workbook.addWorksheet("Pasivo por arrendamiento");

                // Título
                worksheet.addRow(['Pasivo por arrendamiento']);
                worksheet.mergeCells('A1:I1');
                const titleCell = worksheet.getCell('A1');
                titleCell.font = { name: 'Arial', size: 12, bold: true };
                titleCell.alignment = { horizontal: 'center' };

                // Info general
                worksheet.addRow(['']);
                worksheet.mergeCells('A2:I2');
                const cell2 = worksheet.getCell('A2');
                cell2.value = {
                    richText: [
                        { text: 'Contrato: ', font: { bold: true } },
                        { text: `${this.numContrato} | ` },
                        { text: 'Tipo contrato: ', font: { bold: true } },
                        { text: `${this.tipoContrato(this.infoContrato.tipo_contrato)} | ` },
                        { text: 'Tipo activo: ', font: { bold: true } },
                        { text: `${this.tipoActivo()} | ` },
                        { text: 'Generado por: ', font: { bold: true } },
                        { text: `${this.$store.state.userName}` },
                    ]
                };
                cell2.font = { name: 'Arial', size: 12 };
                cell2.alignment = { horizontal: 'center', vertical: 'middle' };

                // Más info
                worksheet.addRow(['']);
                worksheet.mergeCells('A3:I3');
                const cell3 = worksheet.getCell('A3');
                cell3.value = {
                    richText: [
                        { text: 'Fecha inicial: ', font: { bold: true } },
                        { text: `${this.infoContrato.fecha_inicial || 'N/A'} | ` },
                        { text: 'Fecha final: ', font: { bold: true } },
                        { text: `${this.infoContrato.fecha_final || 'N/A'} | ` },
                        { text: 'Fecha generación: ', font: { bold: true } },
                        { text: `${this.fechaActual()}` },
                    ]
                };
                cell3.font = { name: 'Arial', size: 12 };
                cell3.alignment = { horizontal: 'center', vertical: 'middle' };

                worksheet.addRow(['']);
                worksheet.mergeCells('A4:I4');

                // Encabezados
                const headers = this.allFieldsPasivos.map(f => f.label);
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { name: 'Arial', size: 11, bold: true };
                headerRow.alignment = { horizontal: 'center' };
                headerRow.eachCell(cell => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF0073B7' }
                    };
                    cell.font.color = { argb: 'FFFFFFFF' };
                });

                // Datos
                this.listCuotas.forEach(e => {
                    const fila = [];
                    this.allFieldsPasivos.forEach(f => {
                        const valor = e[f.key];
                        if (f.key === 'fecha_pago') {
                            fila.push(valor || '');
                        } else if (f.key === 'tasa') {
                            fila.push(this.formatDecimal(valor));
                        } else {
                            fila.push(valor !== undefined && valor !== null ? valor : 0);
                        }
                    });
                    worksheet.addRow(fila);
                });

                const border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                worksheet.eachRow({ includeEmpty: false }, row => {
                    if ([1, 2, 3, 4].includes(row.number)) return;
                    row.eachCell(cell => {
                        cell.border = border;
                    });
                });

                worksheet.columns.forEach(column => {
                    column.width = 18;
                });

                const file = await workbook.xlsx.writeBuffer();
                const downloadLink = document.createElement('a');
                downloadLink.download = `Pasivo_Arrendamiento_${this.numContrato}.xlsx`;
                downloadLink.href = window.URL.createObjectURL(
                    new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                );
                downloadLink.click();

                this.$toast.success('Excel generado exitosamente.');
            } catch (error) {
                console.error('Error exportando Excel:', error);
                this.$toast.error('Error al generar el Excel.');
            } finally {
                this.isLoading = false;
            }
        }
    },
    mounted() {
        this.numContrato = this.$route.params.contrato;

        ContratoCuota.getContratoCuotaByNumContr(this.numContrato)
            .then((result) => {
                this.listCuotas = result.data.detContrato || [];
                this.showToolbarExtras = this.listCuotas.length > 0;
            })
            .catch((err) => {
                console.error('Error cargando cuotas:', err);
                this.listCuotas = [];
                this.showToolbarExtras = false;
            });

        Contrato.getAllContratos()
            .then((res) => {
                this.listaContratos = res.data.listContratos;
                this.valorCanon = this.listaContratos.find((cont) => cont.nro_contrato == this.numContrato);
                this.canonArrendamiento = this.valorCanon?.canon_arrendamiento;
            })
            .catch((err) => console.error('Error cargando contratos:', err));

        Contrato.leaContrato(this.numContrato)
            .then((res) => {
                this.infoContrato = res.data.contrato[0] || {};
            })
            .catch((err) => console.error('Error cargando info contrato:', err));

        TipoContrato.getAllTipoContrato()
            .then((res) => {
                this.lstTipoContratos = res.data.listTipoContrato || [];
            })
            .catch((err) => console.error('Error cargando tipos contrato:', err));

        TipoActivo.getAllTipoActivo()
            .then((res) => {
                this.lstTipoActivo = res.data.listTipoActivo || [];
            })
            .catch((err) => console.error('Error cargando tipos activo:', err));

        // Por defecto mostrar activo
        this.vistaActiva = 'activo';
    }
}
</script>

<style scoped>
/* Estilos para los botones de alternancia */
.toggle-buttons-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0;
}

.toggle-buttons {
    display: inline-flex;
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 4px;
    gap: 4px;
    border: 1px solid #dee2e6;
}

.toggle-btn {
    padding: 8px 24px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    background-color: transparent;
    transition: all 0.3s ease;
    cursor: pointer;
    white-space: nowrap;
}

.toggle-btn:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.toggle-btn.active {
    background-color: #005a8c;
    color: white;
    box-shadow: 0 2px 8px rgba(0, 115, 183, 0.3);
}

.toggle-btn.active:hover {
    background-color: #005a8c;
}

.toggle-btn:focus {
    outline: none;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-excel-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #016690;
    color: white;
    border: 1px solid #085170;
    transition: background-color 0.2s, opacity 0.2s;
}

.table-toolbar-excel-btn:hover:not(:disabled) {
    background-color: #085170;
    border-color: #0e3a4d;
}

.table-toolbar-excel-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
    .toggle-buttons {
        width: 100%;
        flex-direction: column;
    }
    
    .toggle-btn {
        width: 100%;
        text-align: center;
        padding: 10px 16px;
        font-size: 13px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .toggle-btn {
        font-size: 12px;
        padding: 8px 12px;
    }
}
</style>